// hooks/useRemoveReservation.ts
import { useDispatch, useSelector } from 'react-redux';
import { useRemoveReservationMutation } from '../../api/reservations/reservationsApiSlice';
import Logger from '../helpers/logger';
import ErrorTracker from '../helpers/errorTracker';
import { reservationRemoved } from '../slice/bookingCreation/bookingCreationSlice'; // Import the appropriate action if you have one
import { RootState } from '../store';
import { enqueueNotification } from '../slice/notification/notificationSlice';
import { useTranslation } from 'react-i18next';



export const useRemoveReservation = () => {
  const [removeReservationMutation] = useRemoveReservationMutation();
  const reservations = useSelector((state: RootState) => state.bookingCreation.reservations);
  const dispatch = useDispatch();
  const { t } = useTranslation();
 
  /**
   * `notifySuccess` defaults to "an id was given" (the customer deleted a
   * booking on purpose). A flow releasing its OWN held reservation by id —
   * a step back, a creation result that arrived too late — passes `false`:
   * nothing the customer would call "deleting a booking" happened. A failure
   * is still announced either way.
   */
  const removeReservation = async (
    id?: string,
    options?: { notifySuccess?: boolean }
  ) => {
    const notifySuccess = options?.notifySuccess ?? Boolean(id);
    try {
      let reservationId;
      if (!id) {
        reservationId = reservations?.[reservations.length - 1]?.reservationId;
      } else {
        reservationId = id;
      }
      if (!reservationId) {
        throw new Error('Reservation ID is required');
      }

      await removeReservationMutation({ reservationId }).unwrap();

      dispatch(reservationRemoved({reservationId}));

      if (notifySuccess) {
        dispatch(
          enqueueNotification({
            severity: 'success',
            message: t("success.delete_booking"),
          })
        );
      }

    } catch (error) {
      Logger.for('removeReservation').error(error);
      ErrorTracker.trackException(error as Error);
      dispatch(
        enqueueNotification({
          severity: 'error',
          message: t("error.delete_booking"),
        })
      );

    }
  };

  return removeReservation;
};
